// Shared configuration for game prop display labels, used by both
// GameProps.tsx (the props browser page) and PropDetails.tsx (the chart dropdown).

export function toTitleLabel(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export type CompoundMember = { base: string; rowLabel: string };
export type CompoundGroup = { label: string; members: CompoundMember[] };

export const COMPOUND_GROUPS: Record<string, CompoundGroup> = {
  line: {
    label: "Over/Under",
    members: [
      { base: "over_total", rowLabel: "Over" },
      { base: "under_total", rowLabel: "Under" },
    ],
  },
  reg_line: {
    label: "Regulation Over/Under",
    members: [
      { base: "reg_over_total", rowLabel: "Over" },
      { base: "reg_under_total", rowLabel: "Under" },
    ],
  },
  away_line: {
    label: "Team Over/Under",
    members: [
      { base: "away_over_total", rowLabel: "Over (Away)" },
      { base: "away_under_total", rowLabel: "Under (Away)" },
      { base: "home_over_total", rowLabel: "Over (Home)" },
      { base: "home_under_total", rowLabel: "Under (Home)" },
    ],
  },
  hits_line: {
    label: "Total Hits Over/Under",
    members: [
      { base: "over_total_hits", rowLabel: "Over" },
      { base: "under_total_hits", rowLabel: "Under" },
      // Alias: some sportsbooks report this market as "batting_hits" instead.
      { base: "over_batting_hits", rowLabel: "Over" },
      { base: "under_batting_hits", rowLabel: "Under" },
    ],
  },
  total_hits: {
    label: "Team Total Hits Over/Under",
    members: [
      { base: "away_over_total_hits", rowLabel: "Over (Away)" },
      { base: "away_under_total_hits", rowLabel: "Under (Away)" },
      { base: "home_over_total_hits", rowLabel: "Over (Home)" },
      { base: "home_under_total_hits", rowLabel: "Under (Home)" },
    ],
  },
  total_runs: {
    label: "Team Total Runs Over/Under",
    members: [
      { base: "away_over_total_runs", rowLabel: "Over (Away)" },
      { base: "away_under_total_runs", rowLabel: "Under (Away)" },
      { base: "home_over_total_runs", rowLabel: "Over (Home)" },
      { base: "home_under_total_runs", rowLabel: "Under (Home)" },
    ],
  },
  total_runs_even_odd: {
    label: "Total Runs Odd/Even",
    members: [
      { base: "total_runs_odd", rowLabel: "Odd" },
      { base: "total_runs_even", rowLabel: "Even" },
    ],
  },
  away_corner_kicks_line: {
    label: "Team Total Corners",
    members: [
      { base: "away_corner_kicks_over", rowLabel: "Over (Away)" },
      { base: "away_corner_kicks_under", rowLabel: "Under (Away)" },
      { base: "home_corner_kicks_over", rowLabel: "Over (Home)" },
      { base: "home_corner_kicks_under", rowLabel: "Under (Home)" },
    ],
  },
  shots_on_goal: {
    label: "Team Shots On Goal",
    members: [
      { base: "away_shots_on_goal_over", rowLabel: "Over (Away)" },
      { base: "away_shots_on_goal_under", rowLabel: "Under (Away)" },
      { base: "home_shots_on_goal_over", rowLabel: "Over (Home)" },
      { base: "home_shots_on_goal_under", rowLabel: "Under (Home)" },
      { base: "away_over_shots_on_target", rowLabel: "Over (Away)" },
      { base: "home_over_shots_on_target", rowLabel: "Over (Home)" },
    ],
  },
  spread: {
    label: "Spread",
    members: [
      { base: "away_spread", rowLabel: "Away" },
      { base: "home_spread", rowLabel: "Home" },
    ],
  },
  team_scores: {
    label: "Either Team Scores",
    members: [
      { base: "has_scores", rowLabel: "Yes" },
      { base: "no_scores", rowLabel: "No" },
    ],
  },
  shots: {
    label: "Team Shots",
    members: [
      { base: "away_shots_over", rowLabel: "Over (Away)" },
      { base: "away_shots_under", rowLabel: "Under (Away)" },
      { base: "home_shots_over", rowLabel: "Over (Home)" },
      { base: "home_shots_under", rowLabel: "Under (Home)" },
      { base: "away_over_shots", rowLabel: "Over (Away)" },
      { base: "home_over_shots", rowLabel: "Over (Home)" },
    ],
  },
  regulation_spread: {
    label: "Regulation Spread",
    members: [
      { base: "reg_away_spread", rowLabel: "Away" },
      { base: "reg_home_spread", rowLabel: "Home" },
    ],
  },
  reg_points_even_odd: {
    label: "Points Even/Odd",
    members: [
      { base: "reg_points_even", rowLabel: "Even" },
      { base: "reg_points_odd", rowLabel: "Odd" },
      // Alias: some sportsbooks report this market without the "reg_" prefix.
      { base: "points_even", rowLabel: "Even" },
      { base: "points_odd", rowLabel: "Odd" },
    ],
  },
  regulation_moneyline: {
    label: "Moneyline (2-way)",
    members: [
      { base: "reg_away_outcome", rowLabel: "Away" },
      { base: "reg_home_outcome", rowLabel: "Home" },
    ],
  },
  regulation_moneyline_3way: {
    label: "Regulation Moneyline",
    members: [
      { base: "reg_away_3way_outcome", rowLabel: "Away" },
      { base: "reg_home_3way_outcome", rowLabel: "Home" },
      { base: "reg_draw_outcome", rowLabel: "Draw" },
    ],
  },
  total_points_even: {
    label: "Total Points Odd/Even",
    members: [
      { base: "total_points_even", rowLabel: "Even" },
      { base: "total_points_odd", rowLabel: "Odd" },
    ],
  },
  team_total_points_even: {
    label: "Team Total Points Odd/Even",
    members: [
      { base: "team_total_points_even", rowLabel: "Even" },
      { base: "team_total_points_odd", rowLabel: "Odd" },
    ],
  },
  total_touchdown_over: {
    label: "Total Touchdowns",
    members: [
      { base: "total_touchdowns_over", rowLabel: "Over" },
      { base: "total_touchdowns_under", rowLabel: "Under" },
    ],
  },
  largest_touchdown_yards_over: {
    label: "Largest Touchdown Yards",
    members: [
      { base: "largest_touchdown_yards_over", rowLabel: "Over" },
      { base: "largest_touchdown_yards_under", rowLabel: "Under" },
    ],
  },
  longest_touchdown_yards_over: {
    label: "Longest Touchdown Yards",
    members: [
      { base: "longest_touchdown_yards_over", rowLabel: "Over" },
      { base: "longest_touchdown_yards_under", rowLabel: "Under" },
    ],
  },
  reg_away_line: {
    label: "Regulation Team Over/Under",
    members: [
      { base: "reg_away_over_total", rowLabel: "Over (Away)" },
      { base: "reg_away_under_total", rowLabel: "Under (Away)" },
      { base: "reg_home_over_total", rowLabel: "Over (Home)" },
      { base: "reg_home_under_total", rowLabel: "Under (Home)" },
    ],
  },
  corner_kicks_line: {
    label: "Total Corners",
    members: [
      { base: "corner_kicks_over", rowLabel: "Over" },
      { base: "corner_kicks_under", rowLabel: "Under" },
      { base: "over_total_corners", rowLabel: "Over" },
      { base: "under_total_corners", rowLabel: "Under" },
    ],
  },
  double_chance_moneyline: {
    label: "Double Chance Moneyline",
    members: [
      { base: "double_chance_not_away_outcome", rowLabel: "Away and Draw" },
      { base: "double_chance_not_home_outcome", rowLabel: "Home and Draw" },
      { base: "double_chance_not_draw_outcome", rowLabel: "Home and Away" },
    ],
  },
  // Half group keys use an "h1_"/"h2_" internal prefix (mirroring the q1..q4
  // quarter scheme above) so they collapse under a "1st/2nd Half" parent via
  // PREFIX_GROUPS_DISPLAY. Labels drop the "Half N" prefix since the parent
  // already shows it. Underlying `base` field names are untouched.
  h1_double_chance_moneyline: {
    label: "Double Chance Moneyline",
    members: [
      {
        base: "first_half_double_chance_not_draw_outcome",
        rowLabel: "Home and Away",
      },
      {
        base: "first_half_double_chance_not_away_outcome",
        rowLabel: "Home and Draw",
      },
      {
        base: "first_half_double_chance_not_home_outcome",
        rowLabel: "Away and Draw",
      },
      // Aliases: the game props endpoint names these two outcomes differently
      // than the chart's prop_data endpoint (missing the "double_chance_"
      // infix) — kept as extra members of the same rowLabel so both endpoints
      // resolve to this one group.
      { base: "first_half_not_away_outcome", rowLabel: "Home and Draw" },
      { base: "first_half_not_home_outcome", rowLabel: "Away and Draw" },
    ],
  },
  h2_double_chance_moneyline: {
    label: "Double Chance Moneyline",
    members: [
      {
        base: "second_half_double_chance_not_away_outcome",
        rowLabel: "Home and Draw",
      },
      {
        base: "second_half_double_chance_not_draw_outcome",
        rowLabel: "Home and Away",
      },
      {
        base: "second_half_double_chance_not_home_outcome",
        rowLabel: "Away and Draw",
      },
    ],
  },
  h1_over_under: {
    label: "Over/Under",
    members: [
      { base: "first_half_over_total", rowLabel: "Over" },
      { base: "first_half_under_total", rowLabel: "Under" },
    ],
  },
  h1_either_team_scores: {
    label: "Either Team Scores",
    members: [
      { base: "first_half_has_points", rowLabel: "Yes" },
      { base: "first_half_no_points", rowLabel: "No" },
    ],
  },
  first_to_score: {
    label: "First To Score",
    members: [
      { base: "away_first_to_score", rowLabel: "Away" },
      { base: "home_first_to_score", rowLabel: "Home" },
      { base: "away_first_to_score_yes", rowLabel: "Away" },
      { base: "home_first_to_score_yes", rowLabel: "Home" },
      { base: "first_team_to_score_away", rowLabel: "Away" },
      { base: "first_team_to_score_home", rowLabel: "Home" },
      { base: "first_team_to_score_no_goals", rowLabel: "No Goal" },
    ],
  },
  away_home_has_score: {
    label: "Team Scores",
    members: [
      { base: "away_has_scores", rowLabel: "Away (Yes)" },
      { base: "away_no_scores", rowLabel: "Away (No)" },
      { base: "home_has_scores", rowLabel: "Home (Yes)" },
      { base: "home_no_scores", rowLabel: "Home (No)" },
    ],
  },
  both_teams_score: {
    label: "Both Teams Score",
    members: [
      { base: "both_has_scores", rowLabel: "Yes" },
      { base: "both_no_scores", rowLabel: "No" },
    ],
  },
  both_teams_to_score_yes: {
    label: "Both Teams To Score",
    members: [
      { base: "both_teams_to_score_yes", rowLabel: "Yes" },
      { base: "both_teams_to_score_no", rowLabel: "No" },
    ],
  },
  h2_moneyline: {
    label: "Moneyline",
    members: [
      { base: "second_half_away_outcome", rowLabel: "Away" },
      { base: "second_half_draw_outcome", rowLabel: "Draw" },
      { base: "second_half_home_outcome", rowLabel: "Home" },
    ],
  },
  h2_either_team_scores: {
    label: "Either Team Scores",
    members: [
      { base: "second_half_has_points", rowLabel: "Yes" },
      { base: "second_half_no_points", rowLabel: "No" },
    ],
  },
  h2_points_even_odd: {
    label: "Points Even/Odd",
    members: [
      { base: "second_half_points_even", rowLabel: "Even" },
      { base: "second_half_points_odd", rowLabel: "Odd" },
    ],
  },
  h2_total_points_even_odd: {
    label: "Total Points Even/Odd",
    members: [
      { base: "second_half_total_points_even", rowLabel: "Even" },
      { base: "second_half_total_points_odd", rowLabel: "Odd" },
    ],
  },
  h2_over_under: {
    label: "Over/Under",
    members: [
      { base: "second_half_over_total", rowLabel: "Over" },
      { base: "second_half_under_total", rowLabel: "Under" },
    ],
  },
  h1_team_over_total: {
    label: "Team Over/Under",
    members: [
      { base: "first_half_away_over_total", rowLabel: "Over (Away)" },
      { base: "first_half_away_under_total", rowLabel: "Under (Away)" },
      { base: "first_half_home_over_total", rowLabel: "Over(Home)" },
      { base: "first_half_home_under_total", rowLabel: "Under (Home)" },
    ],
  },
  h2_team_over_total: {
    label: "Team Over/Under",
    members: [
      { base: "second_half_away_over_total", rowLabel: "Over (Away)" },
      { base: "second_half_away_under_total", rowLabel: "Under (Away)" },
      { base: "second_half_home_over_total", rowLabel: "Over(Home)" },
      { base: "second_half_home_under_total", rowLabel: "Under (Home)" },
    ],
  },
  h1_team_scores: {
    label: "Team Scores",
    members: [
      { base: "first_half_away_has_scores", rowLabel: "Yes (Away)" },
      { base: "first_half_away_no_scores", rowLabel: "No (Away)" },
      { base: "first_half_home_has_scores", rowLabel: "Yes (Home)" },
      { base: "first_half_home_no_scores", rowLabel: "No (Home)" },
    ],
  },
  h2_team_scores: {
    label: "Team Scores",
    members: [
      { base: "second_half_away_has_scores", rowLabel: "Yes (Away)" },
      { base: "second_half_away_no_scores", rowLabel: "No (Away)" },
      { base: "second_half_home_has_scores", rowLabel: "Yes (Home)" },
      { base: "second_half_home_no_scores", rowLabel: "No (Home)" },
    ],
  },
  h1_both_teams_score: {
    label: "Both Teams Score",
    members: [
      { base: "first_half_both_has_scores", rowLabel: "Yes" },
      { base: "first_half_both_no_scores", rowLabel: "No" },
    ],
  },
  h1_both_teams_to_score_yes: {
    label: "Both Teams To Score",
    members: [
      { base: "first_half_both_teams_to_score_yes", rowLabel: "Yes" },
      { base: "first_half_both_teams_to_score_no", rowLabel: "No" },
    ],
  },
  h2_both_teams_score: {
    label: "Both Teams Score",
    members: [
      { base: "second_half_both_has_scores", rowLabel: "Yes" },
      { base: "second_half_both_no_scores", rowLabel: "No" },
    ],
  },
  h1_moneyline: {
    label: "Moneyline",
    members: [
      { base: "first_half_away_outcome", rowLabel: "Away" },
      { base: "first_half_draw_outcome", rowLabel: "Draw" },
      { base: "first_half_home_outcome", rowLabel: "Home" },
    ],
  },
  h1_team_spread: {
    label: "Team Spread",
    members: [
      { base: "first_half_away_spread", rowLabel: "Away" },
      { base: "first_half_home_spread", rowLabel: "Home" },
    ],
  },
  h1_points_even_odd: {
    label: "Points Even/Odd",
    members: [
      { base: "first_half_points_even", rowLabel: "Even" },
      { base: "first_half_points_odd", rowLabel: "Odd" },
    ],
  },
  h1_total_points_even_odd: {
    label: "Total Points Even/Odd",
    members: [
      { base: "first_half_total_points_even", rowLabel: "Even" },
      { base: "first_half_total_points_odd", rowLabel: "Odd" },
    ],
  },
  h1_moneyline_alt: {
    label: "Moneyline",
    members: [
      { base: "away_first_half_outcome", rowLabel: "Away" },
      { base: "home_first_half_outcome", rowLabel: "Home" },
    ],
  },
  h1_team_spread_alt: {
    label: "Team Spread",
    members: [
      { base: "away_first_half_spread", rowLabel: "Away" },
      { base: "home_first_half_spread", rowLabel: "Home" },
    ],
  },
  h2_team_spread: {
    label: "Team Spread",
    members: [
      { base: "away_second_half_spread", rowLabel: "Away" },
      { base: "home_second_half_spread", rowLabel: "Home" },
    ],
  },
  // Quarter group keys use a "q1_"/"q2_"/"q3_"/"q4_" internal prefix (rather
  // than matching the underlying field names, which inconsistently put
  // "away_"/"home_" before or after the "_quarter_" segment) so they can all
  // be collapsed under one "1st/2nd/3rd/4th Quarter" parent via
  // PREFIX_GROUPS_DISPLAY, same as the i1..i9 inning groups below. Labels
  // omit the "Quarter N" prefix since the parent group already shows it.
  q1_moneyline: {
    label: "Moneyline",
    members: [
      { base: "away_first_quarter_outcome", rowLabel: "Away" },
      { base: "home_first_quarter_outcome", rowLabel: "Home" },
      { base: "first_quarter_draw_outcome", rowLabel: "Draw" },
    ],
  },
  q1_spread: {
    label: "Team Spread",
    members: [
      { base: "away_first_quarter_spread", rowLabel: "Away" },
      { base: "home_first_quarter_spread", rowLabel: "Home" },
    ],
  },
  q1_team_over_total: {
    label: "Team Total Over/Under",
    members: [
      { base: "away_first_quarter_over_total", rowLabel: "Over (Away)" },
      { base: "away_first_quarter_under_total", rowLabel: "Under (Away)" },
      { base: "home_first_quarter_over_total", rowLabel: "Over (Home)" },
      { base: "home_first_quarter_under_total", rowLabel: "Under (Home)" },
    ],
  },
  q1_over_total: {
    label: "Over/Under",
    members: [
      { base: "first_quarter_over_total", rowLabel: "Over" },
      { base: "first_quarter_under_total", rowLabel: "Under" },
    ],
  },
  q1_total_points_even_odd: {
    label: "Total Points Even/Odd",
    members: [
      { base: "first_quarter_total_points_even", rowLabel: "Even" },
      { base: "first_quarter_total_points_odd", rowLabel: "Odd" },
    ],
  },
  q2_over_total: {
    label: "Over/Under",
    members: [
      { base: "second_quarter_over_total", rowLabel: "Over" },
      { base: "second_quarter_under_total", rowLabel: "Under" },
    ],
  },
  q2_moneyline: {
    label: "Moneyline",
    members: [
      { base: "away_second_quarter_outcome", rowLabel: "Away" },
      { base: "home_second_quarter_outcome", rowLabel: "Home" },
      { base: "second_quarter_draw_outcome", rowLabel: "Draw" },
    ],
  },
  q2_team_over_total: {
    label: "Team Total Over/Under",
    members: [
      { base: "away_second_quarter_over_total", rowLabel: "Over (Away)" },
      { base: "away_second_quarter_under_total", rowLabel: "Under (Away)" },
      { base: "home_second_quarter_over_total", rowLabel: "Over (Home)" },
      { base: "home_second_quarter_under_total", rowLabel: "Under (Home)" },
    ],
  },
  q2_spread: {
    label: "Team Spread",
    members: [
      { base: "away_second_quarter_spread", rowLabel: "Away" },
      { base: "home_second_quarter_spread", rowLabel: "Home" },
    ],
  },
  q2_total_points_even_odd: {
    label: "Total Points Even/Odd",
    members: [
      { base: "second_quarter_total_points_even", rowLabel: "Even" },
      { base: "second_quarter_total_points_odd", rowLabel: "Odd" },
    ],
  },
  q3_over_total: {
    label: "Over/Under",
    members: [
      { base: "third_quarter_over_total", rowLabel: "Over" },
      { base: "third_quarter_under_total", rowLabel: "Under" },
    ],
  },
  q3_total_points_even_odd: {
    label: "Total Points Odd/Even",
    members: [
      { base: "third_quarter_total_points_even", rowLabel: "Even" },
      { base: "third_quarter_total_points_odd", rowLabel: "Odd" },
    ],
  },
  q3_moneyline: {
    label: "Moneyline",
    members: [
      { base: "away_third_quarter_outcome", rowLabel: "Away" },
      { base: "home_third_quarter_outcome", rowLabel: "Home" },
      { base: "third_quarter_draw_outcome", rowLabel: "Draw" },
    ],
  },
  q3_team_over_total: {
    label: "Team Total Over/Under",
    members: [
      { base: "away_third_quarter_over_total", rowLabel: "Over (Away)" },
      { base: "away_third_quarter_under_total", rowLabel: "Under (Away)" },
      { base: "home_third_quarter_over_total", rowLabel: "Over (Home)" },
      { base: "home_third_quarter_under_total", rowLabel: "Under (Home)" },
    ],
  },
  q3_spread: {
    label: "Team Spread",
    members: [
      { base: "away_third_quarter_spread", rowLabel: "Away" },
      { base: "home_third_quarter_spread", rowLabel: "Home" },
    ],
  },
  q4_over_total: {
    label: "Over/Under",
    members: [
      { base: "fourth_quarter_over_total", rowLabel: "Over" },
      { base: "fourth_quarter_under_total", rowLabel: "Under" },
    ],
  },
  q4_moneyline: {
    label: "Moneyline",
    members: [
      { base: "away_fourth_quarter_outcome", rowLabel: "Away" },
      { base: "home_fourth_quarter_outcome", rowLabel: "Home" },
      { base: "fourth_quarter_draw_outcome", rowLabel: "Draw" },
    ],
  },
  q4_team_over_total: {
    label: "Team Total Over/Under",
    members: [
      { base: "away_fourth_quarter_over_total", rowLabel: "Over (Away)" },
      { base: "away_fourth_quarter_under_total", rowLabel: "Under (Away)" },
      { base: "home_fourth_quarter_over_total", rowLabel: "Over (Home)" },
      { base: "home_fourth_quarter_under_total", rowLabel: "Under (Home)" },
    ],
  },
  q4_spread: {
    label: "Team Spread",
    members: [
      { base: "away_fourth_quarter_spread", rowLabel: "Away" },
      { base: "home_fourth_quarter_spread", rowLabel: "Home" },
    ],
  },
  q4_total_points_even_odd: {
    label: "Total Points Even/Odd",
    members: [
      { base: "fourth_quarter_total_points_even", rowLabel: "Even" },
      { base: "fourth_quarter_total_points_odd", rowLabel: "Odd" },
    ],
  },
  h1_team_over_total_alt: {
    label: "Team Over/Under",
    members: [
      { base: "away_first_half_over_total", rowLabel: "Over (Away)" },
      { base: "away_first_half_under_total", rowLabel: "Under (Away)" },
      { base: "home_first_half_over_total", rowLabel: "Over (Home)" },
      { base: "home_first_half_under_total", rowLabel: "Under (Home)" },
    ],
  },
  h2_team_over_total_alt: {
    label: "Team Over/Under",
    members: [
      { base: "away_second_half_over_total", rowLabel: "Over (Away)" },
      { base: "away_second_half_under_total", rowLabel: "Under (Away)" },
      { base: "home_second_half_over_total", rowLabel: "Over (Home)" },
      { base: "home_second_half_under_total", rowLabel: "Under (Home)" },
    ],
  },
  fouls: {
    label: "Team Fouls",
    members: [
      { base: "away_over_fouls", rowLabel: "Over (Away)" },
      { base: "home_over_fouls", rowLabel: "Over (Home)" },
    ],
  },
  away_corners: {
    label: "Team Corners",
    members: [
      { base: "away_over_corners", rowLabel: "Over (Away)" },
      { base: "home_over_corners", rowLabel: "Over (Home)" },
      { base: "away_under_corners", rowLabel: "Under (Away)" },
      { base: "home_under_corners", rowLabel: "Under (Home)" },
    ],
  },
  corners_handicap: {
    label: "Corners Handicap",
    members: [
      { base: "corners_away_handicap", rowLabel: "Away" },
      { base: "corners_home_handicap", rowLabel: "Home" },
    ],
  },
  draw_no_bet: {
    label: "Draw No Bet",
    members: [
      { base: "draw_no_bet_away", rowLabel: "Away" },
      { base: "draw_no_bet_home", rowLabel: "Home" },
    ],
  },
  h1_draw_no_bet: {
    label: "Draw No Bet",
    members: [
      { base: "first_half_draw_no_bet_away", rowLabel: "Away" },
      { base: "first_half_draw_no_bet_home", rowLabel: "Home" },
    ],
  },
  h2_draw_no_bet: {
    label: "Draw No Bet",
    members: [
      { base: "second_half_draw_no_bet_away", rowLabel: "Away" },
      { base: "second_half_draw_no_bet_home", rowLabel: "Home" },
    ],
  },
  win_to_nil: {
    label: "Win to Nil",
    members: [
      { base: "to_win_to_nil_away", rowLabel: "Away" },
      { base: "to_win_to_nil_home", rowLabel: "Home" },
      { base: "team_to_win_to_nil_away", rowLabel: "Away" },
      { base: "team_to_win_to_nil_home", rowLabel: "Home" },
    ],
  },
  win_either_half: {
    label: "Win Either Half",
    members: [
      { base: "to_win_either_half_away", rowLabel: "Away" },
      { base: "to_win_either_half_home", rowLabel: "Home" },
    ],
  },
  win_both_halves: {
    label: "Win Both Halves",
    members: [
      { base: "team_to_win_both_halves_away", rowLabel: "Away" },
      { base: "team_to_win_both_halves_home", rowLabel: "Home" },
    ],
  },
  highest_scoring_half: {
    label: "Highest Scoring Half",
    members: [
      { base: "highest_scoring_half_1st", rowLabel: "1st Half" },
      { base: "highest_scoring_half_2nd", rowLabel: "2nd Half" },
      { base: "highest_scoring_half_equal", rowLabel: "Equal" },
    ],
  },
  halftime_fulltime: {
    label: "Halftime/Fulltime",
    members: [
      { base: "halftime_fulltime_away_away", rowLabel: "Away / Away" },
      { base: "halftime_fulltime_away_draw", rowLabel: "Away / Draw" },
      { base: "halftime_fulltime_away_home", rowLabel: "Away / Home" },
      { base: "halftime_fulltime_draw_away", rowLabel: "Draw / Away" },
      { base: "halftime_fulltime_draw_draw", rowLabel: "Draw / Draw" },
      { base: "halftime_fulltime_draw_home", rowLabel: "Draw / Home" },
      { base: "halftime_fulltime_home_away", rowLabel: "Home / Away" },
      { base: "halftime_fulltime_home_draw", rowLabel: "Home / Draw" },
      { base: "halftime_fulltime_home_home", rowLabel: "Home / Home" },
    ],
  },
  overtime: {
    label: "Overtime",
    members: [
      { base: "overtime_yes", rowLabel: "Yes" },
      { base: "overtime_no", rowLabel: "No" },
    ],
  },
};

// Generate inning-specific compound groups (I1–I9)
for (let i = 1; i <= 9; i++) {
  COMPOUND_GROUPS[`i${i}_moneyline`] = {
    label: "Moneyline (3-way)",
    members: [
      { base: `i${i}_away_3way_outcome`, rowLabel: "Away" },
      { base: `i${i}_home_3way_outcome`, rowLabel: "Home" },
      { base: `i${i}_draw_outcome`, rowLabel: "Draw" },
    ],
  };
  COMPOUND_GROUPS[`i${i}_spread`] = {
    label: "Spread",
    members: [
      { base: `i${i}_away_spread`, rowLabel: "Away" },
      { base: `i${i}_home_spread`, rowLabel: "Home" },
    ],
  };
  COMPOUND_GROUPS[`i${i}_line`] = {
    label: "Over/Under",
    members: [
      { base: `i${i}_over_total`, rowLabel: "Over" },
      { base: `i${i}_under_total`, rowLabel: "Under" },
    ],
  };
  COMPOUND_GROUPS[`i${i}_points_scored`] = {
    label: "Points Scored",
    members: [
      { base: `i${i}_points_yes`, rowLabel: "Yes" },
      { base: `i${i}_points_no`, rowLabel: "No" },
    ],
  };
  COMPOUND_GROUPS[`i${i}_runs_odd_even`] = {
    label: "Runs Odd/Even",
    members: [
      { base: `i${i}_runs_even`, rowLabel: "Even" },
      { base: `i${i}_runs_odd`, rowLabel: "Odd" },
    ],
  };
  COMPOUND_GROUPS[`i${i}_moneyline_2way`] = {
    label: "Moneyline (2-way)",
    members: [
      { base: `i${i}_away_outcome`, rowLabel: "Away" },
      { base: `i${i}_home_outcome`, rowLabel: "Home" },
    ],
  };
  COMPOUND_GROUPS[`i${i}_team_scores`] = {
    label: "Team Scores",
    members: [
      { base: `i${i}_away_scores_yes`, rowLabel: "Away (Yes)" },
      { base: `i${i}_away_scores_no`, rowLabel: "Away (No)" },
      { base: `i${i}_home_scores_yes`, rowLabel: "Home (Yes)" },
      { base: `i${i}_home_scores_no`, rowLabel: "Home (No)" },
    ],
  };
}

// Generate hockey period compound groups (P1–P3)
for (let p = 1; p <= 3; p++) {
  COMPOUND_GROUPS[`p${p}_moneyline`] = {
    label: "Moneyline",
    members: [
      { base: `p${p}_away_outcome`, rowLabel: "Away" },
      { base: `p${p}_home_outcome`, rowLabel: "Home" },
    ],
  };
  COMPOUND_GROUPS[`p${p}_line`] = {
    label: "Over/Under",
    members: [
      { base: `p${p}_over_total`, rowLabel: "Over" },
      { base: `p${p}_under_total`, rowLabel: "Under" },
    ],
  };
  COMPOUND_GROUPS[`p${p}_spread`] = {
    label: "Spread",
    members: [
      { base: `p${p}_away_spread`, rowLabel: "Away" },
      { base: `p${p}_home_spread`, rowLabel: "Home" },
    ],
  };
}

export const PREFIX_GROUPS_DISPLAY: Record<
  string,
  { label: string; prefix: string }
> = {
  i1: { label: "1st Inning", prefix: "i1_" },
  i2: { label: "2nd Inning", prefix: "i2_" },
  i3: { label: "3rd Inning", prefix: "i3_" },
  i4: { label: "4th Inning", prefix: "i4_" },
  i5: { label: "5th Inning", prefix: "i5_" },
  i6: { label: "6th Inning", prefix: "i6_" },
  i7: { label: "7th Inning", prefix: "i7_" },
  i8: { label: "8th Inning", prefix: "i8_" },
  i9: { label: "9th Inning", prefix: "i9_" },
  q1: { label: "1st Quarter", prefix: "q1_" },
  q2: { label: "2nd Quarter", prefix: "q2_" },
  q3: { label: "3rd Quarter", prefix: "q3_" },
  q4: { label: "4th Quarter", prefix: "q4_" },
  h1: { label: "1st Half", prefix: "h1_" },
  h2: { label: "2nd Half", prefix: "h2_" },
  p1: { label: "1st Period", prefix: "p1_" },
  p2: { label: "2nd Period", prefix: "p2_" },
  p3: { label: "3rd Period", prefix: "p3_" },
};

// Reverse map: member base → compound group key
export const BASE_TO_COMPOUND: Record<string, string> = {};
Object.entries(COMPOUND_GROUPS).forEach(([groupKey, { members }]) => {
  members.forEach(({ base }) => {
    BASE_TO_COMPOUND[base] = groupKey;
  });
});

function stripKeySuffix(key: string): { base: string; suffix: string } {
  const match = key.match(/(_point|_price|_lines)$/);
  const suffix = match ? match[0] : "";
  return { base: suffix ? key.slice(0, -suffix.length) : key, suffix };
}

// Recognized sets of mutually-exclusive rowLabels that a chart-page "side"
// dropdown can toggle between. Matching is order-independent (the group's
// members can be defined in any order), but the order given here is the
// canonical display order — its first entry is the default side.
const SIDE_GROUP_LABELS: string[][] = [
  // TODO: Over/Under side selector needs another look before re-enabling —
  // disabled for now, see PropDetails.tsx for the matching commented-out filter changes.
  // ["Over", "Under"],
  ["Yes", "No"],
  ["Even", "Odd"],
  ["Away", "Home"],
  ["Away", "Draw", "Home"],
  ["Away and Draw", "Home and Draw", "Home and Away"],
  ["Yes (Away)", "No (Away)", "Yes (Home)", "No (Home)"],
  // Team Scores (away_home_has_score and the per-inning i{N}_team_scores groups).
  ["Home (Yes)", "Home (No)", "Away (Yes)", "Away (No)"],
  // Halftime/Fulltime (soccer): all 9 halftime result / fulltime result combos.
  [
    "Away / Away",
    "Away / Draw",
    "Away / Home",
    "Draw / Away",
    "Draw / Draw",
    "Draw / Home",
    "Home / Away",
    "Home / Draw",
    "Home / Home",
  ],
];

export type SideGroupInfo = {
  labels: string[]; // canonical order
  currentIndex: number; // index of the currently-selected key's side
  keys: string[]; // key for each side, in `labels` order (suffix preserved)
};

/**
 * For a raw game_odds key belonging to a compound group whose distinct rowLabels
 * exactly match one of the recognized mutually-exclusive sets (Yes/No, Even/Odd,
 * Away/Home, or the 3-way Double Chance set), returns every side's key
 * (preserving any _point/_price/_lines suffix) plus which one is current.
 * A group may list more than one member per rowLabel — different API endpoints
 * sometimes name the same outcome differently (e.g. an "_outcome" vs
 * "_moneyline" suffix) — in which case `isAvailable` (default: always true)
 * picks whichever alias is actually present in the current data, falling back
 * to the first alias if none report as available.
 * Returns null if the key isn't part of such a group.
 */
export function getSideGroupInfo(
  key: string,
  isAvailable: (base: string) => boolean = () => true,
): SideGroupInfo | null {
  const { base, suffix } = stripKeySuffix(key);
  const group = COMPOUND_GROUPS[BASE_TO_COMPOUND[base] ?? ""];
  if (!group) return null;
  const distinctLabels = new Set(group.members.map((m) => m.rowLabel));
  for (const labels of SIDE_GROUP_LABELS) {
    if (distinctLabels.size !== labels.length) continue;
    if (!labels.every((l) => distinctLabels.has(l))) continue;
    const currentMember = group.members.find((m) => m.base === base);
    if (!currentMember) continue;
    const currentIndex = labels.indexOf(currentMember.rowLabel);
    if (currentIndex === -1) continue;
    const keys = labels.map((l) => {
      const candidates = group.members
        .filter((m) => m.rowLabel === l)
        .map((m) => `${m.base}${suffix}`);
      return candidates.find(isAvailable) ?? candidates[0];
    });
    return { labels, currentIndex, keys };
  }
  return null;
}

export type OverUnderPriceOptions = {
  pointKey: string; // always the "Over" side's point — the main dropdown's canonical representative, regardless of which side is currently active
  overPriceKey: string;
  underPriceKey: string;
};

/**
 * For a raw game_odds key belonging to a game/period-level Over/Under total
 * market (any prefix — e.g. "over_total", "first_half_under_total"), returns
 * the Over side's point key plus both sides' price keys, so the chart page can
 * offer a value-type dropdown: "Over/Under Value" (the line's point), "Over
 * Price", and "Under Price". Returns null if the key isn't an over/under total,
 * or if it's a team-specific total (those go through getTeamOverUnderPriceOptions
 * instead, since they need an away/home split too).
 */
export function getOverUnderPriceOptions(
  key: string,
): OverUnderPriceOptions | null {
  const { base } = stripKeySuffix(key);
  // The "_total" suffix can itself be followed by a stat name (e.g.
  // "over_total_hits", "over_total_runs") — keep that stat suffix so the
  // reconstructed keys stay on the same market instead of a bare total.
  // "batting_hits" is a separate alias some sportsbooks use in place of
  // "total_hits" for the same market (see COMPOUND_GROUPS.hits_line).
  const match = base.match(
    /^(.*?)(?:over|under)_(total(?:_\w+)?|batting_hits)$/,
  );
  if (!match) return null;
  const prefix = match[1];
  const statSuffix = match[2];
  if (/(^|_)away(_|$)/.test(base) || /(^|_)home(_|$)/.test(base)) return null;
  return {
    pointKey: `${prefix}over_${statSuffix}_point`,
    overPriceKey: `${prefix}over_${statSuffix}_price`,
    underPriceKey: `${prefix}under_${statSuffix}_price`,
  };
}

/**
 * For a raw player_odds key ("<statBase>_(over|under)_(point|price):<playerId>",
 * e.g. "player_points_over_point:KYLE_TEEL_1_MLB"), returns the Over side's
 * point key plus both sides' price keys — the player-odds equivalent of
 * getOverUnderPriceOptions, accounting for player keys' different suffix
 * grammar (over/under comes before point/price, no "_total" infix) and the
 * trailing ":playerId". Returns null if the key isn't a player over/under stat.
 */
export function getPlayerOverUnderPriceOptions(
  key: string,
): OverUnderPriceOptions | null {
  const colonIdx = key.indexOf(":");
  if (colonIdx === -1) return null;
  const section = key.substring(0, colonIdx);
  const playerId = key.substring(colonIdx + 1);
  const match = section.match(/^(.*)_(?:over|under)_(?:point|price)$/);
  if (!match) return null;
  const statBase = match[1];
  return {
    pointKey: `${statBase}_over_point:${playerId}`,
    overPriceKey: `${statBase}_over_price:${playerId}`,
    underPriceKey: `${statBase}_under_price:${playerId}`,
  };
}

function replaceToken(base: string, from: string, to: string): string {
  return base.replace(
    new RegExp(`(^|_)${from}(_|$)`),
    (_m, p1, p2) => `${p1}${to}${p2}`,
  );
}

/**
 * For a raw game_odds key belonging to a team-specific Over/Under total market
 * (base has both an away/home token and an over/under token — e.g.
 * "away_over_total", "first_half_home_under_total", "away_first_half_over_total"),
 * returns a SideGroupInfo-shaped set of 5 options: "Over/Under Value" (the
 * current side's point, unchanged) plus all 4 combinations of Away/Home x
 * Over/Under price. Returns null if the key isn't a team-specific over/under total.
 */
export function getTeamOverUnderPriceOptions(
  key: string,
): SideGroupInfo | null {
  const { base } = stripKeySuffix(key);
  // As in getOverUnderPriceOptions, "_total" can be followed by a stat name
  // (e.g. "away_over_total_hits") — the away/home ↔ over/under swaps below
  // work on token boundaries regardless, so only the gate checks need to
  // tolerate the trailing suffix.
  if (!/(^|_)(?:over|under)_total(?:_\w+)?$/.test(base)) return null;
  const hasAway = /(^|_)away(_|$)/.test(base);
  const hasHome = /(^|_)home(_|$)/.test(base);
  if (!hasAway && !hasHome) return null;
  const isOver = /(^|_)over_total(?:_\w+)?$/.test(base);
  const overBase = isOver ? base : replaceToken(base, "under", "over");
  const underBase = isOver ? replaceToken(base, "over", "under") : base;
  const awayOverBase = hasAway
    ? overBase
    : replaceToken(overBase, "home", "away");
  const homeOverBase = hasHome
    ? overBase
    : replaceToken(overBase, "away", "home");
  const awayUnderBase = hasAway
    ? underBase
    : replaceToken(underBase, "home", "away");
  const homeUnderBase = hasHome
    ? underBase
    : replaceToken(underBase, "away", "home");

  const labels = [
    "Over/Under Value",
    "Away Over Price",
    "Away Under Price",
    "Home Over Price",
    "Home Under Price",
  ];
  const keys = [
    `${base}_point`,
    `${awayOverBase}_price`,
    `${awayUnderBase}_price`,
    `${homeOverBase}_price`,
    `${homeUnderBase}_price`,
  ];
  const currentIndex = Math.max(keys.indexOf(key), 0);
  return { labels, currentIndex, keys };
}

/**
 * For a raw game_odds key belonging to a spread market (base ends in
 * "_spread", with an "away"/"home" token somewhere in it — e.g. "away_spread",
 * "reg_home_spread", "away_first_half_spread"), returns a SideGroupInfo-shaped
 * set of 4 options: Away/Home (the point values) plus Away Price/Home Price.
 * Returns null if the key isn't a spread base with a recognizable side token.
 */
export function getSpreadSideInfo(key: string): SideGroupInfo | null {
  const { base } = stripKeySuffix(key);
  if (!/(^|_)spread$/.test(base)) return null;
  // Underscore is a \w character, so \b won't separate "away"/"home" from a
  // neighboring "_" — match against underscore-or-string-edge boundaries instead.
  let awayBase: string;
  let homeBase: string;
  if (/(^|_)away(_|$)/.test(base)) {
    awayBase = base;
    homeBase = base.replace(/(^|_)away(_|$)/, (_m, p1, p2) => `${p1}home${p2}`);
  } else if (/(^|_)home(_|$)/.test(base)) {
    homeBase = base;
    awayBase = base.replace(/(^|_)home(_|$)/, (_m, p1, p2) => `${p1}away${p2}`);
  } else {
    return null;
  }
  const labels = ["Away", "Home", "Away Price", "Home Price"];
  const keys = [
    `${awayBase}_point`,
    `${homeBase}_point`,
    `${awayBase}_price`,
    `${homeBase}_price`,
  ];
  const currentIndex = Math.max(keys.indexOf(key), 0);
  return { labels, currentIndex, keys };
}

/**
 * Given a raw game_odds key from the chart API (e.g. "away_first_half_over_total_point"),
 * returns the same display label shown in the GameProps page (e.g. "First Half Team Over/Under").
 * For inning-specific keys, prepends the inning label (e.g. "Inning 3 - Spread").
 */
export function labelForGameOddsKey(key: string): string {
  const base = key.replace(/_point$|_price$/, "");
  const compoundKey = BASE_TO_COMPOUND[base] ?? base;
  const compound = COMPOUND_GROUPS[compoundKey];

  if (compound) {
    // Check if this compound belongs to an inning prefix group
    for (const { prefix, label: prefixLabel } of Object.values(
      PREFIX_GROUPS_DISPLAY,
    )) {
      if (compoundKey.startsWith(prefix)) {
        return `${prefixLabel} - ${compound.label}`;
      }
    }
    return compound.label;
  }

  // No compound match — check for a bare inning prefix
  for (const { prefix, label: prefixLabel } of Object.values(
    PREFIX_GROUPS_DISPLAY,
  )) {
    if (base.startsWith(prefix)) {
      const inner = base
        .substring(prefix.length)
        .replace(/_point$|_price$/, "");
      const innerCompound = COMPOUND_GROUPS[BASE_TO_COMPOUND[inner] ?? inner];
      return innerCompound
        ? `${prefixLabel} - ${innerCompound.label}`
        : `${prefixLabel} - ${toTitleLabel(inner)}`;
    }
  }

  // Final fallback: title-case the base, dropping trailing suffixes that add no meaning
  return toTitleLabel(
    base
      .replace(/_total$/, "")
      .replace(/_spread$/, "")
      .replace(/_handicap$/, "")
      .replace(/_line$/, ""),
  );
}
